import { useMemo, useState } from 'react'
import { ArrowDownRight, ArrowUpRight, X } from 'lucide-react'
import {
  useActions,
  useBrief,
  useCategoryIntel,
  useCompetitors,
  useCompetitorDetail,
  useCreators,
  useDashboard,
} from '../../hooks/useCompetitiveData'
import type { DashboardTopThreatRow } from '../../hooks/useCompetitiveData'
import type { Action } from '../../types/competitive'
import CompetitorDetailPanel from './CompetitorDetailPanel'
import CreatorConvergenceAlert from './CreatorConvergenceAlert'
import EventGraphPanel from './EventGraphPanel'
import IntelligenceBriefBanner from './IntelligenceBriefBanner'
import KeywordAlertPanel from './KeywordAlertPanel'
import AIVisibilityPanel from './AIVisibilityPanel'
import { Sparkline } from './Sparkline'
import TrademarkRadarPanel from './TrademarkRadarPanel'
import WorkerHealthPanel from './WorkerHealthPanel'
import { KPISkeleton, ThreatCardSkeleton } from './Skeleton'
import { humanizeActionFinding } from '../../lib/competitive-utils'
import { canonicalBrandKey, canonicalBrandName, sanitizeNarrativeText, toSentence } from '../../lib/utils'

function scoreColor(score: number): string {
  if (score >= 80) return 'text-[#991b1b] font-extrabold'
  if (score >= 60) return 'text-[#dc2626]'
  if (score >= 40) return 'text-[#d97706]'
  if (score >= 20) return 'text-[var(--gray-600)]'
  return 'text-[var(--gray-400)]'
}

function tierBadgeMeta(tier: string | null): { label: string; classes: string } {
  const normalized = normalizeName(tier)
  if (normalized === 'critical') {
    return {
      label: 'Critical',
      classes: 'border-[#991b1b] bg-[#991b1b] text-white',
    }
  }
  if (normalized === 'high') {
    return {
      label: 'High',
      classes: 'border-[#991b1b] bg-[#fee2e2] text-[#991b1b]',
    }
  }
  if (normalized === 'active' || normalized === 'active_threat') {
    return {
      label: 'Active Threat',
      classes: 'border-[#d97706] bg-[#fef3c7] text-[#92400e]',
    }
  }
  return {
    label: normalized === 'low' ? 'Low' : 'Monitoring',
    classes: 'border-[var(--gray-200)] bg-[var(--gray-100)] text-[var(--gray-500)]',
  }
}

function normalizeName(value: string | null | undefined): string {
  return String(value ?? '').trim().toLowerCase()
}

function deltaBadge(delta: number | null, label: string, invertColors = false) {
  if (delta === null || !Number.isFinite(delta)) {
    return null
  }

  const positiveTone = invertColors
    ? 'inline-flex rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-700'
    : 'inline-flex rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700'
  const negativeTone = invertColors
    ? 'inline-flex rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700'
    : 'inline-flex rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-700'

  if (delta > 0) {
    return (
      <span className={positiveTone}>
        +{Math.round(delta)} {label}
      </span>
    )
  }

  if (delta < 0) {
    return (
      <span className={negativeTone}>
        {Math.round(delta)} {label}
      </span>
    )
  }

  return (
    <span className="inline-flex rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
      0 {label}
    </span>
  )
}

function categoryHealthColor(score: number): string {
  if (score >= 80) return 'text-emerald-600'
  if (score >= 60) return 'text-amber-600'
  if (score >= 40) return 'text-orange-600'
  return 'text-red-600'
}

function categoryHealthInterpretation(score: number): string {
  if (score >= 80) {
    return 'Low competitive pressure. Your position is strong.'
  }
  if (score >= 60) {
    return 'Moderate pressure. Competitors are active but not threatening your position.'
  }
  if (score >= 40) {
    return 'Elevated pressure. Multiple competitors are making moves that could affect your share.'
  }
  return 'High pressure. Immediate competitive threats detected.'
}

function anomalyDensitySummary(anomalyDensity: number, competitorCount: number): string {
  if (anomalyDensity <= 0) {
    return 'No unusual competitor activity this week.'
  }
  const estimatedCompetitors = Math.max(1, Math.round(anomalyDensity * Math.max(1, competitorCount)))
  return `${estimatedCompetitors} competitor${estimatedCompetitors === 1 ? '' : 's'} showing unusual activity.`
}

function formatGrowthRate(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return 'Not available'
  }
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    return 'Not available'
  }
  const rounded = Math.round(parsed * 10) / 10
  return `${rounded >= 0 ? '+' : ''}${rounded}%`
}

function formatMetricDate(value: string | null | undefined): string | null {
  if (!value) {
    return null
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }

  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function normalizeScore(value: number | null | undefined): number {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return 0
  }
  return Number(value)
}

function formatBsr(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return 'Not available'
  }
  return `#${Math.round(value).toLocaleString('en-US')}`
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return 'Not available'
  }
  return `${Math.round(value)}%`
}

function actionTimestamp(action: Action): number {
  const raw = action.executed_at || action.updated_at || action.created_at || null
  if (!raw) {
    return 0
  }
  const parsed = new Date(raw).getTime()
  return Number.isFinite(parsed) ? parsed : 0
}

function isCompletedStatus(status: string | null | undefined): boolean {
  const normalized = normalizeName(status)
  return normalized === 'completed' || normalized === 'done'
}

function isPendingStatus(status: string | null | undefined): boolean {
  const normalized = normalizeName(status)
  return normalized === 'running' || normalized === 'in_progress' || normalized === 'pending'
}

function firstNonEmpty(...values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    if (value && value.trim()) {
      return value.trim()
    }
  }
  return null
}

function actionTypeLabel(actionType: string | null | undefined): string {
  const normalized = normalizeName(actionType)
  if (normalized === 'deal_check') return 'Deal Check'
  if (normalized === 'price_check') return 'Price Check'
  if (normalized === 'creator_scan') return 'Creator Scan'
  if (normalized === 'keyword_defense_check') return 'Keyword Defense'
  if (normalized === 'ai_visibility_action') return 'AI Visibility'
  if (normalized === 'trademark_monitor_action') return 'Trademark Review'
  if (normalized === 'keyword_alert') return 'Keyword Alert'
  return 'Action'
}

function toActionRecord(action: {
  id: number
  action_type: string
  status: string
  finding: string | null
  executed_at: string | null
  recommendation?: string | null
  finding_severity?: string | null
  action_description?: string | null
}): Action {
  return {
    id: action.id,
    trigger_type: '',
    trigger_id: null,
    trigger_severity: '',
    trigger_summary: '',
    action_type: action.action_type,
    action_description: action.action_description || action.finding || 'Action executed.',
    status: action.status,
    finding: action.finding,
    finding_severity: action.finding_severity ?? null,
    recommendation: action.recommendation ?? null,
    competitor_name: null,
    competitor_id: null,
    executed_at: action.executed_at,
    created_at: action.executed_at || new Date().toISOString(),
    updated_at: action.executed_at || new Date().toISOString(),
    brief_date: null,
  }
}

function resolveThreatRecommendation(
  actions: Action[],
  topActionFallback: string | null | undefined
): { text: string; tone: 'action' | 'all_clear' | 'in_progress' } {
  const sortedActions = [...actions].sort((left, right) => actionTimestamp(right) - actionTimestamp(left))

  const confirmed = sortedActions.find(
    (action) => normalizeName(action.finding_severity) === 'confirmed_threat' && firstNonEmpty(action.recommendation)
  )
  if (confirmed) {
    return {
      text: firstNonEmpty(confirmed.recommendation) || 'Action recommended based on confirmed threat.',
      tone: 'action',
    }
  }

  if (sortedActions.some((action) => normalizeName(action.finding_severity) === 'no_action_needed')) {
    return {
      text: 'We checked — no active deals or promotions found. Continue monitoring.',
      tone: 'all_clear',
    }
  }

  if (sortedActions.some((action) => isPendingStatus(action.status))) {
    return {
      text: 'Investigating now — results within 6 hours.',
      tone: 'in_progress',
    }
  }

  const bestActionRecommendation = sortedActions
    .map((action) => firstNonEmpty(action.recommendation, action.action_description, action.finding))
    .find((value) => Boolean(value))
  if (bestActionRecommendation) {
    return {
      text: bestActionRecommendation,
      tone: 'action',
    }
  }

  return {
    text: firstNonEmpty(topActionFallback) || "Prioritize this competitor's highest-momentum channel this week.",
    tone: 'action',
  }
}

function parseFirstNumber(text: string, pattern: RegExp): number | null {
  const match = text.match(pattern)
  if (!match || !match[1]) {
    return null
  }
  const parsed = Number(match[1].replace(/,/g, ''))
  return Number.isFinite(parsed) ? parsed : null
}

function extractCreatorMentions(text: string): { mentions: number | null; creators: number | null } {
  const detailed = text.match(/(\d+)\s*mentions?\s*from\s*(\d+)\s*unique/i)
  if (detailed?.[1] && detailed?.[2]) {
    return {
      mentions: Number(detailed[1]),
      creators: Number(detailed[2]),
    }
  }

  const creators = parseFirstNumber(text, /(\d+)\s*(?:macro\/mega|macro|mega|unique)?\s*creators?/i)
  return { mentions: null, creators }
}

function extractBsrMove(text: string): { from: number | null; to: number | null } {
  const directional = text.match(/#?([\d,]+)\s*(?:→|->|to)\s*#?([\d,]+)/i)
  if (directional?.[1] && directional?.[2]) {
    return {
      from: Number(directional[1].replace(/,/g, '')),
      to: Number(directional[2].replace(/,/g, '')),
    }
  }

  const from = parseFirstNumber(text, /baseline\s*#?([\d,]+)/i)
  const to = parseFirstNumber(text, /current\s*#?([\d,]+)/i)
  return { from, to }
}

function humanizeWhatHappened(
  rawSignal: string | null | undefined,
  competitorName: string,
  threat: DashboardTopThreatRow
): string | null {
  const signal = firstNonEmpty(rawSignal)
  if (!signal) {
    return null
  }

  const score = Math.round(normalizeScore(threat.total_score))
  const delta = Math.round(normalizeScore(threat.score_delta_7d))
  const signalLower = signal.toLowerCase()

  if (/tiktok|creator/.test(signalLower)) {
    const { mentions, creators } = extractCreatorMentions(signal)
    if (creators && creators > 0 && mentions && mentions > 0) {
      return `${mentions.toLocaleString('en-US')} TikTok mentions from ${creators.toLocaleString('en-US')} creators for ${competitorName} in the last 7 days.`
    }
    if (creators && creators > 0) {
      return `${creators.toLocaleString('en-US')} creators mentioned ${competitorName} this week, signaling accelerating social momentum.`
    }
    if (delta !== 0) {
      return `${competitorName}'s TikTok momentum moved ${delta > 0 ? 'up' : 'down'} by ${Math.abs(delta)} threat-score points this week.`
    }
    return `${competitorName} is drawing elevated TikTok attention this week.`
  }

  if (
    (/bsr/i.test(signalLower) && /(improv|climb|rising|moved|up|rank)/i.test(signalLower)) ||
    /rank moved from\s*#?\d[\d,]*\s*to\s*#?\d[\d,]*/i.test(signal)
  ) {
    const bsrMove = extractBsrMove(signal)
    if (bsrMove.from && bsrMove.to) {
      return `${competitorName}'s Amazon bestseller rank improved from #${bsrMove.from.toLocaleString('en-US')} to #${bsrMove.to.toLocaleString('en-US')}, which usually means stronger sales velocity.`
    }
    return `${competitorName} is climbing in Amazon rankings, which points to stronger unit movement this week.`
  }

  if (/ai.*visibility|replaced.*buttah|ai.*recommendation/.test(signalLower)) {
    return `${competitorName} is appearing where Buttah used to appear in AI shopping recommendations, reducing brand visibility for AI-led discovery.`
  }

  return `${signal.replace(/\s+/g, ' ').trim()} (threat score ${score}${delta !== 0 ? `, ${delta > 0 ? '+' : ''}${delta} vs last week` : ''}).`
}

function buildRecommendation(
  threat: DashboardTopThreatRow,
  actions: Action[],
  topActionFallback: string | null | undefined
): string {
  const completedAction = actions.find(
    (action) => ['completed', 'done'].includes(normalizeName(action.status)) && action.finding
  )
  if (completedAction?.finding) {
    const finding = completedAction.finding
    const paidMatch = finding.match(/(\d+\.?\d*)%\s*paid/i)
    const paidRate = paidMatch ? parseFloat(paidMatch[1]) : null

    if (paidRate !== null && paidRate < 5) {
      return `Prioritize creator outreach - ${threat.name}'s TikTok momentum is organic (only ${paidRate.toFixed(1)}% paid). Identify overlapping creators and lock them into Buttah partnerships before ${threat.name} does.`
    }
    if (paidRate !== null && paidRate >= 5) {
      return `Monitor ${threat.name}'s paid creator campaign (${paidRate.toFixed(1)}% paid). This is likely a planned push - watch for corresponding Amazon deal/coupon activity and prepare a defensive promotion if their BSR improves.`
    }
  }

  if (topActionFallback && topActionFallback.trim().length > 20) {
    return topActionFallback
  }

  const signal = (threat.top_signal || '').toLowerCase()
  const delta = threat.score_delta_7d
  const score = threat.total_score

  if (/tiktok|creator/.test(signal)) {
    if (score >= 25) {
      return `${threat.name} has high creator momentum (score ${score}) with ${delta !== null && delta > 0 ? `+${delta} points this week` : 'rising activity'}. Run a creator overlap scan, then outreach the shared creators before they go exclusive with ${threat.name}.`
    }
    return `${threat.name} is gaining TikTok traction but hasn't hit critical mass yet (score ${score}). Keep monitoring - focus your creator budget on your own campaigns rather than reacting.`
  }

  if (/bsr|amazon|rank/.test(signal)) {
    if (threat.latest_bsr && threat.latest_bsr < 10000) {
      return `${threat.name} is climbing Amazon rankings (BSR #${threat.latest_bsr.toLocaleString()}). Check if they're running a coupon, Subscribe & Save deal, or Lightning Deal - and consider a defensive promotion on your top ASINs.`
    }
    return `${threat.name} shows Amazon ranking movement. Review their listing for new A+ content, price changes, or advertising spend increases.`
  }

  if (/keyword|search|serp/.test(signal)) {
    return `${threat.name} is appearing in your branded keyword searches. Audit your Sponsored Brand ads and ensure "buttah" keyword bids are high enough to maintain top-of-search position.`
  }

  if (score >= 20) {
    return `${threat.name} is active across multiple channels (score ${score}). Prioritize a listing defense check on Fenty Skin and respond quickly to any coupon, price, or deal pressure.`
  }
  return `${threat.name} activity is low (score ${score}). No immediate action needed - system will alert if signals escalate.`
}

function buildThreatNarrative(
  threat: DashboardTopThreatRow,
  actions: Action[],
  topActionFallback: string | null | undefined
): { whatHappened: string; whatWeDid: string; whatYouShouldDo: string } {
  const whatHappened = toSentence(
    humanizeWhatHappened(threat.top_signal, threat.name, threat),
    `${threat.name} is active this week with measurable competitive movement.`
  )

  const sortedActions = [...actions].sort((left, right) => actionTimestamp(right) - actionTimestamp(left))
  const completedActions = sortedActions.filter((action) => isCompletedStatus(action.status))
  const latestCompletedAction = completedActions[0] ?? sortedActions[0]
  const latestFinding = firstNonEmpty(latestCompletedAction?.finding)
  const whatWeDid = latestCompletedAction
    ? latestFinding
      ? humanizeActionFinding(latestCompletedAction.action_type, latestFinding, threat.name)
      : `We ran ${actionTypeLabel(latestCompletedAction.action_type)} and logged no material issue.`
    : threat.total_score < 15
      ? `${threat.name} score is below the action threshold (${threat.total_score}). We'll trigger an automated check if activity picks up.`
      : `Monitoring ${threat.name} - automated checks will run when new signals arrive.`

  const whatYouShouldDo = buildRecommendation(threat, sortedActions, topActionFallback)

  return {
    whatHappened,
    whatWeDid,
    whatYouShouldDo,
  }
}

function CompetitorDrawer({
  selected,
  open,
  onClose,
}: {
  selected: DashboardTopThreatRow | null
  open: boolean
  onClose: () => void
}) {
  const { data, isLoading, error } = useCompetitorDetail(selected?.competitor_id ?? null)
  const detail = data?.competitor ?? null
  const detailError = error instanceof Error ? error.message : null

  return (
    <div className={`fixed inset-0 z-50 ${open ? 'pointer-events-auto' : 'pointer-events-none'}`} aria-hidden={!open}>
      <div
        className={`absolute inset-0 bg-slate-900/30 transition-opacity duration-200 ${open ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />
      <aside
        className={`absolute inset-y-0 right-0 h-full w-full max-w-xl overflow-y-auto bg-white shadow-2xl transition-transform duration-200 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Competitor Detail</p>
            <h3 className="text-lg font-semibold text-slate-900">{selected?.name ?? 'Competitor'}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-200 bg-white p-2 text-slate-600 hover:bg-slate-100"
            aria-label="Close competitor details"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">Threat Score</p>
              <p className={`mt-1 text-2xl font-bold ${scoreColor(normalizeScore(selected?.total_score ?? null))}`}>
                {Math.round(normalizeScore(selected?.total_score ?? null))}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs uppercase tracking-wide text-slate-500">Latest BSR</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{formatBsr(selected?.latest_bsr)}</p>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <p className="text-xs uppercase tracking-wide text-slate-500">Top Signal</p>
            <p className="mt-1 text-sm text-slate-700">
              {selected?.top_signal?.trim() || detail?.top_signal_summary || 'No recent signal captured'}
            </p>
          </div>

          {detailError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{detailError}</div>
          )}

          {isLoading ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              Loading competitor details...
            </div>
          ) : (
            detail && (
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Alert Tier</p>
                  <p className="mt-1 text-sm font-semibold text-slate-900 capitalize">{detail.alert_tier || 'watch'}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs uppercase tracking-wide text-slate-500">TikTok</p>
                  <p className="mt-1 text-sm text-slate-900">{detail.tiktok_handle || 'Not set'}</p>
                </div>
              </div>
            )
          )}
        </div>
      </aside>
    </div>
  )
}

function FreshnessBadge({ timestamp }: { timestamp: string | null | undefined }) {
  if (!timestamp) return null
  const date = new Date(timestamp)
  if (isNaN(date.getTime())) return null
  const hoursAgo = Math.round((Date.now() - date.getTime()) / (1000 * 60 * 60))
  const label =
    hoursAgo < 1
      ? 'Just updated'
      : hoursAgo < 24
        ? `Updated ${hoursAgo}h ago`
        : `Updated ${Math.round(hoursAgo / 24)}d ago`
  const cls =
    hoursAgo >= 72
      ? 'text-red-600 bg-red-50 border-red-200'
      : hoursAgo >= 24
        ? 'text-amber-700 bg-amber-50 border-amber-200'
        : 'text-slate-500 bg-slate-50 border-slate-200'
  return <span className={`rounded border px-1.5 py-0.5 text-xs ${cls}`}>{label}</span>
}

export default function DashboardTab() {
  const { data, isLoading, error } = useDashboard()
  const { data: briefResponse, isLoading: isBriefLoading, error: briefError } = useBrief()
  const { data: actionsResponse } = useActions()
  const { data: competitorsResponse } = useCompetitors()
  const { data: creatorsResponse } = useCreators()
  const { data: categoryIntelResponse, isLoading: isCategoryLoading, error: categoryError } = useCategoryIntel(7)
  const [detailCompetitorId, setDetailCompetitorId] = useState<number | null>(null)
  const [detailCompetitorName, setDetailCompetitorName] = useState<string>('')
  const [showDeepIntel, setShowDeepIntel] = useState(false)

  const dashboardError = error instanceof Error ? error.message : null
  const briefErrorMessage = briefError instanceof Error ? briefError.message : null
  const categoryErrorMessage = categoryError instanceof Error ? categoryError.message : null
  const brief = briefResponse?.brief ?? null
  const briefGeneratedAt = briefResponse?.generated_at ?? brief?.generated_at ?? null
  const latestCategory = categoryIntelResponse?.latest ?? null

  const topThreats = useMemo(() => {
    const rows = data?.topThreats ?? []
    const deduped = new Map<string, DashboardTopThreatRow>()

    for (const threat of rows) {
      const canonicalName = canonicalBrandName(threat.name)
      const key = canonicalBrandKey(canonicalName)
      const normalizedThreat = { ...threat, name: canonicalName }
      const existing = deduped.get(key)

      if (!existing || normalizeScore(normalizedThreat.total_score) > normalizeScore(existing.total_score)) {
        deduped.set(key, normalizedThreat)
      }
    }

    return Array.from(deduped.values())
      .sort((left, right) => normalizeScore(right.total_score) - normalizeScore(left.total_score))
      .slice(0, 3)
  }, [data?.topThreats])
  const allActions = useMemo(() => actionsResponse?.actions ?? [], [actionsResponse?.actions])
  const convergenceAlerts = useMemo(
    () => creatorsResponse?.convergenceAlerts ?? [],
    [creatorsResponse?.convergenceAlerts]
  )

  const sovEntries = useMemo(
    () => {
      const apiEntries = Array.isArray(categoryIntelResponse?.sov_entries)
        ? categoryIntelResponse?.sov_entries
        : []

      const rawEntries =
        apiEntries.length > 0
          ? apiEntries.map((entry) => ({
              brand: String(entry.brand ?? 'Unknown'),
              share:
                entry.share === null || entry.share === undefined || !Number.isFinite(Number(entry.share))
                  ? null
                  : Number(entry.share),
              label: String(entry.label ?? entry.brand ?? 'Unknown'),
              is_buttah: Boolean(entry.is_buttah),
              is_baseline: Boolean(entry.is_baseline),
            }))
          : [
              {
                brand: 'Buttah Skin',
                share: null,
                label: 'Buttah (baseline)',
                is_buttah: true,
                is_baseline: true,
              },
              ...Object.entries(categoryIntelResponse?.competitor_sov_summary ?? {})
                .sort((left, right) => Number(right[1]) - Number(left[1]))
                .map(([brand, share]) => ({
                  brand,
                  share: Number(share),
                  label: brand,
                  is_buttah: false,
                  is_baseline: false,
                })),
            ]

      const buttahEntry = rawEntries.find((entry) => entry.is_buttah) ?? null
      const competitorMap = new Map<string, { brand: string; share: number; label: string }>()

      for (const entry of rawEntries) {
        if (entry.is_buttah) {
          continue
        }
        const canonicalName = canonicalBrandName(entry.brand)
        const key = canonicalBrandKey(canonicalName)
        if (!key) {
          continue
        }

        const current = competitorMap.get(key)
        const shareValue = entry.share ?? 0
        if (!current) {
          competitorMap.set(key, { brand: canonicalName, label: canonicalName, share: shareValue })
          continue
        }

        current.share += shareValue
      }

      const dedupedCompetitorEntries = Array.from(competitorMap.values())
        .map((entry) => ({
          brand: entry.brand,
          label: entry.label,
          share: Math.round(entry.share * 10) / 10,
          is_buttah: false,
          is_baseline: false,
        }))
        .sort((left, right) => Number(right.share) - Number(left.share))

      return [
        buttahEntry ?? {
          brand: 'Buttah Skin',
          share: null,
          label: 'Buttah (baseline)',
          is_buttah: true,
          is_baseline: true,
        },
        ...dedupedCompetitorEntries,
      ]
    },
    [categoryIntelResponse?.sov_entries, categoryIntelResponse?.competitor_sov_summary]
  )

  const topSovLeader = useMemo(
    () => sovEntries.find((entry) => !entry.is_buttah && entry.share !== null) ?? null,
    [sovEntries]
  )

  const activeThreats = data?.kpis.activeThreats
  const topMover = data?.kpis.topMover
  const buttahAmazonRank = data?.kpis.buttahAmazonRank
  const keywordDefense = data?.kpis.keywordDefense
  const competitorsMonitored = useMemo(() => {
    const competitors = competitorsResponse?.competitors ?? []
    const keys = new Set<string>()
    for (const competitor of competitors) {
      const key = canonicalBrandKey(competitor.brand_name)
      if (key) {
        keys.add(key)
      }
    }
    return keys.size
  }, [competitorsResponse?.competitors])
  const recommendedActionMap = useMemo(() => {
    const map = new Map<number, string>()
    const threats = brief?.top_threats ?? []
    for (const threat of threats) {
      const topAction = threat.recommended_actions?.[0] || null
      if (topAction) {
        map.set(Number(threat.competitor_id), topAction)
      }
    }
    return map
  }, [brief?.top_threats])

  const actionsByThreat = useMemo(() => {
    const byCompetitorId = new Map<number, Action[]>()
    const byName = new Map<string, Action[]>()

    for (const action of allActions) {
      if (action.competitor_id && Number.isFinite(action.competitor_id)) {
        const current = byCompetitorId.get(action.competitor_id) ?? []
        current.push(action)
        byCompetitorId.set(action.competitor_id, current)
      }
      const normalizedName = normalizeName(action.competitor_name)
      if (normalizedName) {
        const current = byName.get(normalizedName) ?? []
        current.push(action)
        byName.set(normalizedName, current)
      }
    }

    const matchMap = new Map<number, Action[]>()
    for (const threat of topThreats) {
      const fromId = byCompetitorId.get(threat.competitor_id) ?? []
      const fromName = byName.get(normalizeName(threat.name)) ?? []
      const deduped = new Map<number, Action>()
      for (const action of [...fromId, ...fromName]) {
        deduped.set(action.id, action)
      }
      if (deduped.size > 0) {
        matchMap.set(
          threat.competitor_id,
          Array.from(deduped.values()).sort((left, right) => actionTimestamp(right) - actionTimestamp(left))
        )
      }
    }
    return matchMap
  }, [allActions, topThreats])

  const hasCategoryGrowth =
    latestCategory?.category_growth_rate !== null &&
    latestCategory?.category_growth_rate !== undefined &&
    Number.isFinite(Number(latestCategory.category_growth_rate))

  const activeThreatsPrevious = Math.max(
    0,
    Math.round(normalizeScore(activeThreats?.value) - normalizeScore(activeThreats?.delta))
  )

  if (detailCompetitorId !== null) {
    return (
      <CompetitorDetailPanel
        competitorId={detailCompetitorId}
        competitorName={detailCompetitorName || 'Competitor'}
        onBack={() => setDetailCompetitorId(null)}
      />
    )
  }

  return (
    <>
      <section className="space-y-6">
        {dashboardError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{dashboardError}</div>
        )}

        <section className="space-y-4">
          <div className="competitive-section-header">
            <p style={{ borderBottom: 'none', marginBottom: 0 }}>At a Glance</p>
            <FreshnessBadge timestamp={data?.freshness?.signalsAt} />
          </div>

          {isBriefLoading ? (
            <div className="animate-pulse overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="h-20 bg-[var(--patterniq-navy-900)]" />
              <div className="space-y-3 p-5">
                <div className="h-4 w-2/3 rounded bg-slate-200" />
                <div className="h-4 w-full rounded bg-slate-200" />
                <div className="h-4 w-5/6 rounded bg-slate-200" />
              </div>
            </div>
          ) : brief ? (
            <IntelligenceBriefBanner
              brief={brief}
              generatedAt={briefGeneratedAt}
              showThreatCards={false}
              showKeywordAlerts={false}
            />
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
              The intelligence brief generates daily at 6am EST. First brief will appear after the next worker run.
              {briefErrorMessage ? ` (${briefErrorMessage})` : ''}
            </div>
          )}

          <div className="space-y-3">
            <h3 className="competitive-panel-title">Weekly Pulse</h3>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {isLoading ? (
                <>
                  <KPISkeleton />
                  <KPISkeleton />
                  <KPISkeleton />
                  <KPISkeleton />
                </>
              ) : (
                <>
                  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <p className="competitive-support-label">Active Threats</p>
                    <p className="competitive-body-text mt-1">
                      {(() => {
                        const count = Math.round(normalizeScore(activeThreats?.value))
                        if (count === 0) return 'No significant competitive activity this week. Focus on your own growth.'
                        const topName = topThreats[0]?.name || 'Unknown'
                        const topScore = Math.round(normalizeScore(topThreats[0]?.total_score))
                        const topSignal = (topThreats[0]?.top_signal || '').toLowerCase()
                        const channel = /tiktok|creator/.test(topSignal)
                          ? 'TikTok creator activity'
                          : /bsr|amazon|rank/.test(topSignal)
                            ? 'Amazon ranking movement'
                            : /keyword/.test(topSignal)
                              ? 'keyword intrusion'
                              : 'cross-channel signals'
                        return `${count} competitor${count > 1 ? 's' : ''} making moves. ${topName} leads (score ${topScore}) driven by ${channel}. Review their threat card below.`
                      })()}
                    </p>
                    <p className="mt-2 text-3xl font-bold text-slate-900">{Math.round(normalizeScore(activeThreats?.value))}</p>
                    <div className="mt-1">
                      <Sparkline data={data?.kpis.weeklyTrend?.activeThreats ?? []} trend="up" />
                    </div>
                    {Math.round(normalizeScore(activeThreats?.value)) > 0 ? (
                      <div className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
                        → Review threat cards below and take the top recommended action
                      </div>
                    ) : (
                      <div className="mt-2 rounded bg-green-50 px-2 py-1 text-xs text-green-800">
                        → No action needed — focus on your own growth initiatives
                      </div>
                    )}
                    <p className="mt-1 text-xs text-slate-500">
                      {Math.round(normalizeScore(activeThreats?.value)) === 0
                        ? 'All clear.'
                        : Math.round(normalizeScore(activeThreats?.value)) <= 2
                          ? 'Normal range.'
                          : 'Elevated — review threats.'}
                    </p>
                    {activeThreatsPrevious > 0 ? (
                      <p className="mt-2 text-xs text-slate-500">
                        vs {activeThreatsPrevious.toLocaleString('en-US')} last week
                      </p>
                    ) : null}
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <p className="competitive-support-label">Biggest Move</p>
                    <p className="competitive-body-text mt-1">
                      {(() => {
                        if (!topMover?.name) return 'Tracking all competitor movement.'
                        const delta = topMover.delta ?? 0
                        const score = Math.round(normalizeScore(topMover.score))
                        if (delta < -10) {
                          return `${topMover.name}'s threat score dropped ${Math.abs(delta)} pts to ${score}. Their competitive pressure is fading — good news for Buttah. No action needed unless they rebound.`
                        }
                        if (delta < 0) {
                          return `${topMover.name} cooled off slightly (${delta} pts, now ${score}). Monitor but don't react — focus your energy on bigger threats above.`
                        }
                        if (delta > 10) {
                          return `${topMover.name}'s threat score surged +${delta} pts to ${score}. This is a significant escalation — check their threat card below for what's driving it and recommended actions.`
                        }
                        if (delta > 0) {
                          return `${topMover.name} ticked up +${delta} pts to ${score}. Mild increase — keep an eye on it but no urgent action.`
                        }
                        return `${topMover.name} is the most active mover (score ${score}). No significant change this week.`
                      })()}
                    </p>
                    {topMover ? (
                      <>
                        <p className="mt-2 truncate text-base font-semibold text-slate-700">
                          {topMover.name ?? 'Unknown competitor'}
                        </p>
                        <div className="mt-1 flex items-center gap-1">
                          {(topMover.delta ?? 0) >= 0 ? (
                            <ArrowUpRight className="h-5 w-5 text-red-600" />
                          ) : (
                            <ArrowDownRight className="h-5 w-5 text-emerald-600" />
                          )}
                          <p className={`text-3xl font-bold ${scoreColor(normalizeScore(topMover.score))}`}>
                            {Math.round(normalizeScore(topMover.score))}
                          </p>
                        </div>
                      </>
                    ) : (
                      <p className="mt-2 text-3xl font-bold text-slate-900">—</p>
                    )}
                    <div className="mt-1">
                      <Sparkline data={topMover?.trend ?? []} trend="flat" />
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      {topMover?.latest_bsr && topMover?.previous_bsr && topMover.latest_bsr < topMover.previous_bsr
                        ? `Amazon rank improved from ${formatBsr(topMover.previous_bsr)} to ${formatBsr(topMover.latest_bsr)}.`
                        : topMover
                          ? `Score is a composite of TikTok, Amazon, and keyword signals. ${(topMover.delta ?? 0) < 0 ? 'Decline = less pressure on Buttah.' : 'Rise = increasing competitive pressure.'}`
                          : 'No mover this week.'}
                    </p>
                    <div className="mt-2">{deltaBadge(topMover?.delta ?? null, 'this week', true)}</div>
                    {topMover && (
                      <p className="mt-1 text-xs text-slate-500">
                        {(() => {
                          const score = Math.round(normalizeScore(topMover.score))
                          if (score >= 25) return 'Score 25+ = high threat. Review their card below for recommended actions.'
                          if (score >= 15) return 'Score 15-24 = moderate threat. Monitor but no urgent action needed.'
                          if (score >= 5) return 'Score 5-14 = low threat. Competitor is active but not a significant risk right now.'
                          return 'Score below 5 = minimal activity. No action needed.'
                        })()}
                      </p>
                    )}
                    {topMover && (topMover.delta ?? 0) > 5 ? (
                      <div className="mt-2 rounded bg-red-50 px-2 py-1 text-xs text-red-800">
                        → Click their threat card to see what&apos;s driving the surge
                      </div>
                    ) : topMover && (topMover.delta ?? 0) < -5 ? (
                      <div className="mt-2 rounded bg-green-50 px-2 py-1 text-xs text-green-800">
                        → Competitor retreating — maintain your current strategy
                      </div>
                    ) : (
                      <div className="mt-2 rounded bg-slate-50 px-2 py-1 text-xs text-slate-600">
                        → No significant movement — stay the course
                      </div>
                    )}
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <p className="competitive-support-label">Your Amazon Rank</p>
                    <p className="competitive-body-text mt-1">
                      Buttah bestseller rank across tracked skincare ASINs.
                    </p>
                    <p className="mt-2 text-3xl font-bold text-slate-900">{formatBsr(buttahAmazonRank?.current)}</p>
                    <div className="mt-1">
                      <Sparkline data={buttahAmazonRank?.trend ?? []} trend="flat" />
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      {(() => {
                        const current = buttahAmazonRank?.current
                        const previous = buttahAmazonRank?.previous
                        if (current != null && previous != null) {
                          const improved = current < previous
                          const delta = Math.abs(current - previous)
                          if (improved) {
                            return `Improved ${delta.toLocaleString()} spots from #${previous.toLocaleString()} last week. Lower rank = more sales.`
                          }
                          return `Slipped ${delta.toLocaleString()} spots from #${previous.toLocaleString()} last week. Monitor for competitor promotions.`
                        }
                        if (current != null) {
                          if (current < 5000) {
                            return `Rank #${current.toLocaleString()} — strong position in skincare. Top 5K typically indicates consistent daily sales.`
                          }
                          if (current < 15000) {
                            return `Rank #${current.toLocaleString()} — solid mid-range position. Consistent sellers typically land between #5K-#15K.`
                          }
                          if (current < 50000) {
                            return `Rank #${current.toLocaleString()} — room to improve. Consider promotions or ad spend to drive rank higher.`
                          }
                          return `Rank #${current.toLocaleString()} — lower rank indicates opportunity for growth. Compare to competitors' BSR below.`
                        }
                        return 'Rank data collecting — will populate after the next BSR scan.'
                      })()}
                    </p>
                    {buttahAmazonRank?.current != null &&
                    buttahAmazonRank?.previous != null &&
                    buttahAmazonRank.current > buttahAmazonRank.previous ? (
                      <div className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
                        → Consider running a coupon or Lightning Deal to recover rank
                      </div>
                    ) : buttahAmazonRank?.current != null && buttahAmazonRank.current < 10000 ? (
                      <div className="mt-2 rounded bg-green-50 px-2 py-1 text-xs text-green-800">
                        → Strong rank position — maintain current listing and ad strategy
                      </div>
                    ) : (
                      <div className="mt-2 rounded bg-slate-50 px-2 py-1 text-xs text-slate-600">
                        → Review your top ASINs in the Amazon tab for optimization opportunities
                      </div>
                    )}
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    <p className="competitive-support-label">Keyword Defense</p>
                    <p className="competitive-body-text mt-1">
                      Share of tracked keywords where Buttah holds the top position.
                    </p>
                    <p className="mt-2 text-3xl font-bold text-slate-900">{formatPercent(keywordDefense?.value)}</p>
                    <div className="mt-1">
                      <Sparkline data={keywordDefense?.trend ?? []} trend="flat" />
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      {(() => {
                        const current = keywordDefense?.value
                        const previous = keywordDefense?.previous
                        if (current != null && previous != null) {
                          const diff = current - previous
                          if (diff > 0) {
                            return `Up from ${formatPercent(previous)} last week — Buttah is gaining keyword ground. Keep defending branded terms.`
                          }
                          if (diff < 0) {
                            return `Down from ${formatPercent(previous)} last week — competitors are winning more keyword positions. Review sponsored ad bids.`
                          }
                          return `Holding steady at ${formatPercent(current)} vs last week. Keyword position is stable.`
                        }
                        if (current != null) {
                          if (current >= 80) {
                            return `${formatPercent(current)} — strong keyword defense. Buttah dominates most tracked search terms.`
                          }
                          if (current >= 60) {
                            return `${formatPercent(current)} — solid but competitors hold ${formatPercent(100 - current)} of positions. Review the Keywords tab for gaps.`
                          }
                          return `${formatPercent(current)} — competitors are winning more keyword positions than Buttah. Urgent: review sponsored brand ad strategy.`
                        }
                        return 'Keyword data collecting — will populate after the next keyword scan.'
                      })()}
                    </p>
                    {keywordDefense?.total ? (
                      <p className="mt-2 text-xs text-slate-500">{keywordDefense.total} tracked keywords this week.</p>
                    ) : null}
                    {keywordDefense?.value != null && keywordDefense.value < 70 ? (
                      <div className="mt-2 rounded bg-red-50 px-2 py-1 text-xs text-red-800">
                        → Competitors winning {formatPercent(100 - keywordDefense.value)} of keyword positions — increase sponsored brand bids
                      </div>
                    ) : keywordDefense?.value != null && keywordDefense.value >= 80 ? (
                      <div className="mt-2 rounded bg-green-50 px-2 py-1 text-xs text-green-800">
                        → Strong keyword defense — maintain current bid strategy
                      </div>
                    ) : (
                      <div className="mt-2 rounded bg-slate-50 px-2 py-1 text-xs text-slate-600">
                        → Review the Keywords tab to identify any gaps in branded search defense
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <div className="competitive-section-header">
            <p style={{ borderBottom: 'none', marginBottom: 0 }}>What Changed This Week</p>
            <FreshnessBadge timestamp={data?.freshness?.threatsAt} />
          </div>

          <div className="space-y-3">
            <h3 className="competitive-panel-title">Threat Cards</h3>
            {isLoading ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                <ThreatCardSkeleton />
                <ThreatCardSkeleton />
                <ThreatCardSkeleton />
              </div>
            ) : topThreats.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
                No significant threat movers this week.
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {topThreats.map((threat) => {
                  const threatActions =
                    actionsByThreat.get(threat.competitor_id) ??
                    (threat.actions ?? []).map((action) => toActionRecord(action))
                  const recommendation = resolveThreatRecommendation(
                    threatActions,
                    recommendedActionMap.get(threat.competitor_id) || null
                  )
                  const narrative = buildThreatNarrative(threat, threatActions, recommendation.text)
                  const scoreDelta = Math.round(normalizeScore(threat.score_delta_7d))
                  const tierBadge = tierBadgeMeta(threat.alert_tier)

                  return (
                    <article
                      key={threat.competitor_id}
                      className="overflow-hidden rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:shadow-md"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="competitive-panel-title truncate">{threat.name}</p>
                          <span
                            className={`mt-1 inline-flex rounded-md border px-2 py-0.5 text-xs font-semibold ${tierBadge.classes}`}
                          >
                            {tierBadge.label}
                          </span>
                        </div>
                        <div className="text-right">
                          <p className={`text-3xl font-bold ${scoreColor(normalizeScore(threat.total_score))}`}>
                            {Math.round(normalizeScore(threat.total_score))}
                          </p>
                          {scoreDelta !== 0 ? (
                            <p className={`text-xs font-semibold ${scoreDelta > 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                              {scoreDelta > 0 ? '▲' : '▼'}
                              {Math.abs(scoreDelta)} this week
                            </p>
                          ) : null}
                        </div>
                      </div>

                      <div className="mt-4 border-t border-slate-200 pt-3">
                        <p className="competitive-support-label">What Happened</p>
                        <p className="competitive-body-text competitive-line-clamp-3 mt-1">{narrative.whatHappened}</p>
                      </div>

                      <div className="mt-3 border-t border-slate-200 pt-3">
                        <p className="competitive-support-label">What We Did</p>
                        <p className="competitive-body-text competitive-line-clamp-3 mt-1">{narrative.whatWeDid}</p>
                      </div>

                      <div className="mt-3 border-t border-slate-200 pt-3">
                        <p className="competitive-support-label">What You Should Do</p>
                        <p className="competitive-body-text-strong competitive-line-clamp-4 mt-1">{narrative.whatYouShouldDo}</p>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setDetailCompetitorId(threat.competitor_id)
                          setDetailCompetitorName(threat.name)
                        }}
                        className="mt-3 text-sm font-semibold text-[var(--patterniq-navy-900)] hover:underline"
                      >
                        View Full Profile →
                      </button>
                    </article>
                  )
                })}
              </div>
            )}
          </div>

          {brief?.keyword_alerts && brief.keyword_alerts.length > 0 && (
            <div className="space-y-2">
              <h3 className="competitive-support-label">Keyword Intrusion Alerts</h3>
              <p className="competitive-body-text">
                Someone else&apos;s product is showing up when customers search for your brand name on Amazon.
              </p>
              <KeywordAlertPanel alerts={brief.keyword_alerts} />
            </div>
          )}

          {convergenceAlerts.length > 0 && (
            <div className="space-y-1">
              <h3 className="competitive-support-label">Creator Convergence Alerts</h3>
              {convergenceAlerts.slice(0, 2).map((alert) => {
                const competitorName =
                  canonicalBrandName(alert.competitor_name || '') || alert.competitor_name || 'Unknown competitor'
                const rawNarrative = alert.description || (alert as { narrative?: string | null }).narrative || ''
                const narrative = sanitizeNarrativeText(
                  rawNarrative.replace(/\bShea\s+Moisture\b/gi, 'SheaMoisture')
                )

                return (
                  <CreatorConvergenceAlert
                    key={alert.id}
                    competitorName={competitorName}
                    creatorCount={alert.creator_count}
                    timeWindowDays={7}
                    narrative={narrative}
                  />
                )
              })}
            </div>
          )}

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="competitive-panel-title">
                  Melanin-Rich Skincare — Category Health
                </h3>
                <p className="competitive-body-text mt-1">7-day category pressure and market movement.</p>
              </div>
              {formatMetricDate(latestCategory?.metric_date) && (
                <span className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600">
                  As of {formatMetricDate(latestCategory?.metric_date)}
                </span>
              )}
            </div>

            {categoryErrorMessage && (
              <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {categoryErrorMessage}
              </div>
            )}

            {isCategoryLoading ? (
              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                Loading category intelligence...
              </div>
            ) : latestCategory ? (
              <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="competitive-support-label">Category Health Score</p>
                  <p className={`mt-2 text-4xl font-bold ${categoryHealthColor(normalizeScore(latestCategory.category_health_score))}`}>
                    {Math.round(normalizeScore(latestCategory.category_health_score))}
                    <span className="ml-1 text-lg text-slate-500">/100</span>
                  </p>
                  <p className="competitive-body-text mt-2">
                    So what: {categoryHealthInterpretation(normalizeScore(latestCategory.category_health_score))}
                  </p>
                  <p className="competitive-body-text mt-2">
                    {anomalyDensitySummary(
                      normalizeScore(latestCategory.anomaly_density),
                      Math.max(1, competitorsMonitored)
                    )}
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 lg:col-span-2">
                  <p className="competitive-support-label">Category Trend</p>
                  {hasCategoryGrowth ? (
                    <div className="mt-2 flex items-center gap-2">
                      {Number(latestCategory.category_growth_rate) >= 0 ? (
                        <ArrowUpRight className="h-6 w-6 text-emerald-600" />
                      ) : (
                        <ArrowDownRight className="h-6 w-6 text-red-600" />
                      )}
                      <p className="text-3xl font-bold text-slate-900">{formatGrowthRate(latestCategory.category_growth_rate)}</p>
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-slate-600">Growth trend unavailable for this period.</p>
                  )}
                  <p className="competitive-body-text mt-2">
                    Top competitor by share of voice:{' '}
                    <span className="font-semibold text-slate-800">
                      {topSovLeader ? `${topSovLeader.brand} (${Number(topSovLeader.share).toFixed(1)}%)` : 'Not available'}
                    </span>
                  </p>
                </div>
              </div>
            ) : (
              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                Category intelligence is not available yet.
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="competitive-panel-title">Share of Voice (7d Avg)</h3>
            <div className="mt-3 space-y-2">
              {sovEntries.length === 0 ? (
                <p className="competitive-body-text">No share data available.</p>
              ) : (
                <div className="relative space-y-3">
                  <div className="pointer-events-none absolute inset-y-0 left-1/2 border-l border-dashed border-[var(--gray-300)]" />
                  {sovEntries.map((entry, index) => {
                    const share = entry.share === null ? null : Math.max(0, Math.min(100, Number(entry.share)))
                    const widthPct = share === null ? 0 : share
                    const barStyle = {
                      width: `${widthPct}%`,
                      backgroundColor: entry.is_buttah ? 'var(--patterniq-navy-700)' : 'var(--gray-400)',
                    }

                    return (
                      <div key={`${entry.brand}-${index}`}>
                        <div className="mb-1 flex items-center justify-between gap-3">
                          <span className={`truncate text-sm ${entry.is_buttah ? 'font-bold text-[var(--patterniq-navy-700)]' : 'text-[var(--gray-700)]'}`}>
                            {entry.is_buttah ? (
                              <>
                                {entry.brand}{' '}
                                <span className="font-semibold text-[var(--patterniq-navy-500)]">(your brand)</span>
                              </>
                            ) : (
                              entry.label
                            )}
                          </span>
                          <span className={`text-sm font-semibold ${entry.is_buttah ? 'text-[var(--patterniq-navy-700)]' : 'text-[var(--gray-700)]'}`}>
                            {share === null ? 'baseline' : `${share.toFixed(1)}%`}
                          </span>
                        </div>
                        <div className="h-2.5 overflow-hidden rounded-full border border-[var(--gray-200)] bg-[var(--gray-100)]">
                          {share !== null ? (
                            <div className="h-full rounded-full" style={barStyle} />
                          ) : (
                            <div className="h-full w-full border-t border-dashed border-[var(--gray-400)]" />
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
            <p className="competitive-support-label mt-3">Dashed line marks the 50% majority threshold.</p>
          </div>
        </section>

        <section className="space-y-4">
          <div className="competitive-section-header">
            <p style={{ borderBottom: 'none', marginBottom: 0 }}>Deep Intel</p>
            <button
              onClick={() => setShowDeepIntel((v) => !v)}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              {showDeepIntel ? 'Hide ▲' : 'Show ▼'}
            </button>
          </div>

          {showDeepIntel ? (
            <>
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <AIVisibilityPanel />
                <TrademarkRadarPanel />
              </div>
              <EventGraphPanel />
              <WorkerHealthPanel />
            </>
          ) : null}
        </section>
      </section>

    </>
  )
}
